import { useSignal } from "@preact/signals-react";
import { useState, useMemo } from "react";
import { toast } from "react-toastify";
import { FaSearch, FaChevronDown } from "react-icons/fa";
import { currencyWithPrefix } from "../../../utils/currencyUtils";
import { formatDateToBR } from "../../../utils/DateUtils";
import { formatDocNumber } from "../../../utils/documentUtils";
import DateFilter from "../../molecules/DateFilter";
import PageHeader from "../../molecules/PageHeader";
import Loading from "../../ions/Loading";
import { OperationsSummaryModal } from "../../molecules/OperationsSummaryModal";

interface Lote {
    date_mov: string;
    batch_seqno: string;
    contract_number: string;
    num_adlib: number;
    our_number: string;
    doc_sacado: string;
    debt_expire_date: string;
    nominal_value: number;
    principal_value: number;
    fee_value: number;
    permanence_value: number;
    discount_value: number;
    paid_value: number;
    liquidated_value: number;
    liquidation_type: string;
    modality: string;
    liquidation_bank: string;
    username: string;
}

const API_URL = process.env.REACT_APP_DIMENSA_CHUPIM_API_URL || '';
const API_KEY = process.env.REACT_APP_DIMENSA_CHUPIM_API_KEY || '';

const DimensaLoteList: React.FC = () => {
    const lotes = useSignal<Lote[]>([]);
    const isLoading = useSignal(false);
    const [dateField, setDateField] = useState(new Date().toISOString().split('T')[0]);
    const [statusField, setStatusField] = useState('');
    const [showSummaryModal, setShowSummaryModal] = useState(false);

    const fetchLotes = async () => {
        if (!dateField || !statusField) {
            toast.error("Selecione a data e o status do lote");
            return;
        }

        isLoading.value = true;
        try {
            const response = await fetch(`${API_URL}/backoffice/listarLote`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    date_field: dateField,
                    status_field: statusField
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API Error Response:', errorText);
                throw new Error(`Erro: ${response.status}`);
            }

            const data = await response.json();
            console.log('API Response:', data);

            if (data.success && data.content) {
                lotes.value = data.content;
                toast.success(`${lotes.value.length} lote(s) encontrado(s)`);
            } else {
                lotes.value = [];
                toast.info("Nenhum lote encontrado");
            }
        } catch (error: any) {
            console.error('Fetch Error:', error);
            toast.error(`Erro ao buscar lotes: ${error.message}`);
            lotes.value = [];
        } finally {
            isLoading.value = false;
        }
    };

    const formatCurrency = (value: number) => currencyWithPrefix(value || 0);

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        // Se já tem hora, usa direto; senão adiciona T00:00:00
        const dateToFormat = dateString.includes('T') ? dateString : dateString + 'T00:00:00';
        return formatDateToBR(dateToFormat);
    };

    const formatModality = (modality: string) => {
        const map: Record<string, string> = {
            'EMPR PJ': 'Empréstimo PJ',
            'DESSCOOB': 'Sessão sem Regresso',
            'EM03F365': 'CPRF Varejo'
        };
        return map[modality] || modality || '-';
    };

    const summary = useMemo(() => {
        console.log('Calculando summary, lotes:', lotes.value);
        const total = lotes.value.reduce((sum, lote) => {
            const value = Number(lote.paid_value) || 0;
            return sum + value;
        }, 0);
        
        const bankTotals = lotes.value.reduce((acc, lote) => {
            const bank = lote.liquidation_bank || 'Não informado';
            if (!acc[bank]) acc[bank] = 0;
            acc[bank] += Number(lote.paid_value) || 0;
            return acc;
        }, {} as Record<string, number>);

        console.log('Summary calculado:', { total, bankTotals });
        return {
            total,
            bankTotals: Object.entries(bankTotals).map(([bank, total]) => ({ bank, total }))
        };
    }, [lotes.value]);

    return (
        <div className="px-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            <div className="grid grid-cols-1">
                {isLoading.value && <Loading />}

                <PageHeader
                    title="Lotes de Liquidação DIMENSA"
                    apiVersion=""
                    breadcrumb={[
                        { label: 'Financeiro', url: '/financial' },
                        { label: 'Lotes DIMENSA', url: '#' }
                    ]}
                >
                    <div className="pt-4"></div>
                </PageHeader>

                <div className={`bg-gray-50 dark:bg-slate-700 p-4 rounded-lg mb-4 ${isLoading.value ? 'opacity-50 pointer-events-none' : ''}`}>
                    <div className="flex items-center gap-4 justify-between">
                        <div className="flex items-center gap-4">
                            <DateFilter
                                label="Data Movimento:"
                                value={dateField}
                                onChange={setDateField}
                            />
                            <div className="flex items-center gap-2 relative">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Status do Lote:
                                </label>
                                <div className="relative">
                                    <select
                                        value={statusField}
                                        onChange={(e) => setStatusField(e.target.value)}
                                        className="px-3 py-2 pr-8 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none cursor-pointer"
                                        style={{
                                            backgroundImage: 'none',
                                            WebkitAppearance: 'none',
                                            MozAppearance: 'none'
                                        }}
                                    >
                                        <option value="">Selecione...</option>
                                        <option value="FE">Lançado</option>
                                        <option value="EF">Efetivado</option>
                                    </select>
                                    <FaChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 pointer-events-none text-xs" />
                                </div>
                            </div>
                            <button
                                onClick={fetchLotes}
                                disabled={isLoading.value}
                                className="text-white py-2 px-4 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                <FaSearch />
                                Consultar Lotes
                            </button>
                            {lotes.value.length > 0 && (
                                <span className="px-3 py-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded font-medium text-sm">
                                    Total: {lotes.value.length} lote(s)
                                </span>
                            )}
                        </div>
                        {lotes.value.length > 0 && (
                            <button
                                onClick={() => setShowSummaryModal(true)}
                                className="text-white py-2 px-4 rounded-lg bg-yellow-600 hover:bg-yellow-700 flex items-center gap-2"
                            >
                                Resumo Operações
                            </button>
                        )}
                    </div>
                </div>

                {lotes.value.length === 0 ? (
                    <div className="text-center py-20 text-gray-500 dark:text-gray-400 italic">
                        Selecione a data, status e clique em Consultar para visualizar lotes
                    </div>
                ) : (
                    <div className={`overflow-x-auto bg-white dark:bg-slate-800 shadow border border-gray-200 dark:border-slate-600 ${isLoading.value ? 'opacity-50 pointer-events-none' : ''}`}>
                        <table className="min-w-full text-sm">
                            <thead className="text-xs text-slate-500 uppercase bg-gray-50 dark:bg-slate-800 dark:text-gray-50">
                                <tr>
                                    <th className="px-2 py-2 text-left border-b">Lote</th>
                                    <th className="px-2 py-2 text-left border-b">Modalidade</th>
                                    <th className="px-2 py-2 text-left border-b">Contrato</th>
                                    <th className="px-2 py-2 text-left border-b">Nosso Nº</th>
                                    <th className="px-2 py-2 text-center border-b">Data Venc.</th>
                                    <th className="px-2 py-2 text-right border-b">V. Nominal</th>
                                    <th className="px-2 py-2 text-right border-b">Principal</th>
                                    <th className="px-2 py-2 text-right border-b">Juros</th>
                                    <th className="px-2 py-2 text-right border-b">Permanência</th>
                                    <th className="px-2 py-2 text-right border-b">Desconto</th>
                                    <th className="px-2 py-2 text-right border-b">Valor Pago</th>
                                    <th className="px-2 py-2 text-right border-b">V. Liquidado</th>
                                    <th className="px-2 py-2 text-left border-b">Tipo Liq.</th>
                                    <th className="px-2 py-2 text-left border-b">Banco</th>
                                    <th className="px-2 py-2 text-left border-b">Sacado</th>
                                    <th className="px-2 py-2 text-left border-b">Usuário</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lotes.value.map((lote, index) => (
                                    <tr
                                        key={index}
                                        className="border-b border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700"
                                    >
                                        <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{lote.batch_seqno || '-'}</td>
                                        <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{formatModality(lote.modality)}</td>
                                        <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{`${lote.contract_number || ''}-${lote.num_adlib ?? ''}`}</td>
                                        <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{lote.our_number || '-'}</td>
                                        <td className="px-2 py-2 text-center text-gray-900 dark:text-gray-100">{formatDate(lote.debt_expire_date)}</td>
                                        <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(lote.nominal_value)}</td>
                                        <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(lote.principal_value)}</td>
                                        <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(lote.fee_value)}</td>
                                        <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(lote.permanence_value)}</td>
                                        <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(lote.discount_value)}</td>
                                        <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(lote.paid_value)}</td>
                                        <td className="px-2 py-2 text-right text-gray-900 dark:text-gray-100">{formatCurrency(lote.liquidated_value)}</td>
                                        <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{lote.liquidation_type || '-'}</td>
                                        <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{lote.liquidation_bank || '-'}</td>
                                        <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{formatDocNumber(lote.doc_sacado)}</td>
                                        <td className="px-2 py-2 text-gray-900 dark:text-gray-100">{lote.username || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <OperationsSummaryModal
                isOpen={showSummaryModal}
                onClose={() => setShowSummaryModal(false)}
                totalLiquidated={summary.total}
                bankTotals={summary.bankTotals}
            />
        </div>
    );
};

export default DimensaLoteList;